import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { BreadcrumbService } from 'src/app/services/breadcrumb/breadcrumb.service';

const APP_NAME = 'Incidite';

/**
 * Keeps the browser tab title in sync with the active page:
 * "<page> | Incidite", where <page> is the most specific breadcrumb label
 * (a section like "Incidents" or a resolved entity name), prefixed by its
 * entity on an entity's sub-page ("Acme Status · Widget | Incidite").
 * Re-translates on a language switch. Instantiated once from AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class DocumentTitleService {
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly transloco = inject(TranslocoService);
  private readonly title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.initTitleSync();
  }

  private initTitleSync(): void {
    toObservable(this.breadcrumbs.pageTitle)
      .pipe(
        // entity names aren't keys; transloco hands them back unchanged,
        // same as the topbar's `| transloco` on each crumb
        switchMap(labels =>
          labels.length
            ? combineLatest(
                labels.map(label =>
                  this.transloco.selectTranslate<string>(label)
                )
              )
            : of([])
        ),
        map(parts =>
          parts.length ? `${parts.join(' · ')} | ${APP_NAME}` : APP_NAME
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(title => this.title.setTitle(title));
  }
}
